// src/components/ConsentCallback.js
// Landing route for the Microsoft admin-consent redirect (Security Insights connector).
// Microsoft returns here with ?admin_consent=True&tenant=...&state=<base64 orgId> on success,
// or ?error=...&error_description=...&state=... on failure/decline.
//
// Because consent runs as a same-tab navigation, localStorage (selectedOrganization, user) is
// preserved across the round-trip, so we simply forward to Integration Settings with a result flag
// that page then verifies. `state` carries the orgId (set by the backend consent-url endpoint) and
// is used only as a fallback to restore org context if it was somehow lost.
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function ConsentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const adminConsent = params.get("admin_consent");
    const error = params.get("error");
    const errorDesc = params.get("error_description") || error;

    // Fallback only: if the selected org was lost, recover the id from state so the next page has
    // an Organization-Id. Same-tab flow normally keeps selectedOrganization intact.
    const stateRaw = params.get("state");
    if (stateRaw && !localStorage.getItem("selectedOrganization")) {
      try {
        const orgId = parseInt(atob(stateRaw), 10);
        if (Number.isFinite(orgId)) {
          localStorage.setItem("selectedOrganization", JSON.stringify({ organizationId: orgId }));
        }
      } catch { /* ignore malformed state */ }
    }

    if (error) {
      navigate(`/settings/integrations?consent=error&desc=${encodeURIComponent(errorDesc || "Admin consent failed.")}`, { replace: true });
    } else if (adminConsent && adminConsent.toLowerCase() === "true") {
      navigate("/settings/integrations?consent=success", { replace: true });
    } else {
      navigate("/settings/integrations", { replace: true });
    }
  }, [params, navigate]);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "#475569", fontSize: 14 }}>
      Completing admin consent…
    </div>
  );
}
